import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { plans } from "@/config/stripePlans";
import { invokeInternal } from "./invoke";
import TrialLengthFields from "./TrialLengthFields";
import {
  DEFAULT_TRIAL_DAYS,
  defaultTrialPlan,
  TRIAL_PLAN_KEYS,
  trialDaysLeft,
  trialIsRunning,
  validTrialDays,
  type TrialAccount,
} from "./trials";

interface TrialAdjustDialogProps {
  account: TrialAccount | null;
  onClose: () => void;
  onSaved: () => void;
}

type Result = { success?: boolean; error?: string; support_access?: string };

const TrialAdjustDialog = ({ account, onClose, onSaved }: TrialAdjustDialogProps) => {
  const [plan, setPlan] = useState("premium");
  const [days, setDays] = useState(DEFAULT_TRIAL_DAYS);
  const [neverExpires, setNeverExpires] = useState(false);
  const [includeBookings, setIncludeBookings] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [busy, setBusy] = useState<null | "save" | "end">(null);

  useEffect(() => {
    if (!account) return;
    setPlan(defaultTrialPlan(account.subscription_tier));
    // Start from what's left, so changing only the plan doesn't move the end date.
    setDays(
      trialIsRunning(account) && account.next_billing_date
        ? Math.max(1, trialDaysLeft(account.next_billing_date))
        : DEFAULT_TRIAL_DAYS,
    );
    setNeverExpires(trialIsRunning(account) && !account.next_billing_date);
    setIncludeBookings(account.has_bookings_addon);
    setBusinessName("");
  }, [account]);

  const run = async (action: "set" | "end") => {
    if (!account) return;
    setBusy(action === "set" ? "save" : "end");
    try {
      const { data, error } = await invokeInternal<Result>("internal-trial-accounts", {
        action,
        business_id: account.business_id,
        ...(action === "set"
          ? {
              plan,
              days,
              never_expires: neverExpires,
              include_bookings: includeBookings,
              business_name: businessName,
            }
          : {}),
      });
      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || "Could not update this trial.");
      toast.success(action === "set" ? "Trial updated" : "Trial ended");
      if (data.support_access === "failed") {
        toast.warning("Dibs support access could not be added. Try saving again.");
      }
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not update this trial.");
    } finally {
      setBusy(null);
    }
  };

  const running = !!account && trialIsRunning(account);
  const canSave = neverExpires || validTrialDays(days);

  return (
    <Dialog open={account !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="admin-modal-content">
        <DialogHeader className="admin-modal-header">
          <DialogTitle>{running ? "Adjust trial" : "Start a trial"}</DialogTitle>
          <DialogDescription>
            {account?.business_name || "Unnamed business"} · {account?.email}
          </DialogDescription>
        </DialogHeader>

        {account && !account.business_name && (
          <div className="space-y-1.5">
            <Label htmlFor="trial-business-name">Business name</Label>
            <Input
              id="trial-business-name"
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
            />
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="trial-plan">Plan during the trial</Label>
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger id="trial-plan">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRIAL_PLAN_KEYS.map((key) => (
                <SelectItem key={key} value={key}>
                  {plans[key]?.name ?? key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TrialLengthFields
          idPrefix="trial"
          days={days}
          neverExpires={neverExpires}
          onChange={(patch) => {
            if (patch.days !== undefined) setDays(patch.days);
            if (patch.neverExpires !== undefined) setNeverExpires(patch.neverExpires);
          }}
        />

        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={includeBookings}
            onCheckedChange={(value) => setIncludeBookings(value === true)}
          />
          Include Dibs Bookings
        </label>

        <DialogFooter className="gap-2">
          {running && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="standard-action-button neutral-action-button sm:mr-auto"
              onClick={() => void run("end")}
              disabled={busy !== null}
            >
              {busy === "end" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              End trial now
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            className="standard-action-button"
            onClick={() => void run("set")}
            disabled={busy !== null || !canSave}
          >
            {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {running ? "Save changes" : "Start trial"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrialAdjustDialog;
